import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useEffect, useState } from "react";

export type GenerateProposalPdfDelivery = "open" | "download";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Saved proposal preference — used as the dialog default only. */
  defaultHideItemPricing: boolean;
  /**
   * Called when staff confirms. Parent is responsible for opening/downloading the PDF
   * with this render-only override. Does not mutate the proposal.
   */
  onConfirm: (opts: { hideItemPricing: boolean }) => void;
};

/**
 * Staff PDF generation dialog.
 * Choosing show/hide item pricing affects only this generated PDF —
 * it never writes to the proposal's saved display settings.
 */
export default function GenerateProposalPdfDialog({
  open,
  onOpenChange,
  defaultHideItemPricing,
  onConfirm,
}: Props) {
  const [hideItemPricing, setHideItemPricing] = useState(defaultHideItemPricing);

  useEffect(() => {
    if (open) setHideItemPricing(defaultHideItemPricing);
  }, [open, defaultHideItemPricing]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="generate-proposal-pdf-dialog">
        <DialogHeader>
          <DialogTitle>Generate Proposal PDF</DialogTitle>
          <DialogDescription>
            This affects only this generated PDF and does not modify the saved proposal.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={hideItemPricing ? "hide" : "show"}
          onValueChange={(value) => setHideItemPricing(value === "hide")}
          className="gap-3 py-1"
        >
          <div className="flex items-start gap-3 rounded-md border border-border/70 px-3 py-2.5">
            <RadioGroupItem value="show" id="pdf-show-item-pricing" className="mt-0.5" />
            <Label htmlFor="pdf-show-item-pricing" className="cursor-pointer font-normal leading-snug">
              Show individual item pricing
            </Label>
          </div>
          <div className="flex items-start gap-3 rounded-md border border-border/70 px-3 py-2.5">
            <RadioGroupItem value="hide" id="pdf-hide-item-pricing" className="mt-0.5" />
            <Label htmlFor="pdf-hide-item-pricing" className="cursor-pointer font-normal leading-snug">
              Hide individual item pricing
            </Label>
          </div>
        </RadioGroup>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onConfirm({ hideItemPricing });
              onOpenChange(false);
            }}
            data-testid="generate-proposal-pdf-confirm"
          >
            Generate PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
